/**
 * Ticket WK1: dünne Hüllen der Wahlkalender-Grundlage. Logik in
 * `wahlkalender.server.ts`. Alle Wege sind Geschäftsführung und Redaktion
 * vorbehalten (dort geprüft).
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { BUNDESLAENDER } from "@/lib/contacts";
import {
  MAYOR_OFFICES,
  MUNICIPALITY_MAX_ROWS,
  PROPOSAL_FIELDS,
  RUNS_AGAIN_VALUES,
} from "@/lib/wahlkalender";
import type {
  ImportReport,
  MunicipalityRow,
} from "@/lib/wahlkalender.server";
import type { TermLength } from "@/lib/wahlkalender";

const bundeslandSchema = z.enum(BUNDESLAENDER as [string, ...string[]]);

const skippedSchema = z
  .array(
    z.object({
      rowNumber: z.number().int().min(1),
      name: z.string().trim().max(200),
      reason: z.string().trim().max(300),
    }),
  )
  .max(5000)
  .default([]);

export const listTermLengths = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<TermLength[]> => {
    const { listTermLengthsOnServer } = await import("@/lib/wahlkalender.server");
    return listTermLengthsOnServer(context.supabase, context.userId);
  });

export const listMunicipalities = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        bundesland: bundeslandSchema.nullable().default(null),
        // Ticket WK2, Schritt 7: Filter und Freitextsuche der Gemeindeliste.
        district: z.string().trim().max(200).nullable().default(null),
        runsAgain: z
          .enum(["unbekannt", "ja", "nein"] as [string, ...string[]])
          .nullable()
          .default(null),
        period: z
          .enum(["alle", "ueberfaellig", "6", "12", "24"] as [string, ...string[]])
          .default("alle"),
        search: z.string().trim().max(120).default(""),
        limit: z.number().int().min(1).max(1000).default(250),
        // Ticket WK12: Startposition der angeforderten Seite.
        offset: z.number().int().min(0).max(100000).default(0),
      })
      .parse(input ?? {}),

  )
  .handler(async ({ context, data }): Promise<{ rows: MunicipalityRow[]; total: number }> => {
    const { listMunicipalitiesOnServer } = await import("@/lib/wahlkalender.server");
    return listMunicipalitiesOnServer(context.supabase, context.userId, data as never);
  });

export const importMunicipalities = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        rowsRead: z.number().int().min(0).max(MUNICIPALITY_MAX_ROWS * 2),
        rows: z
          .array(
            z.object({
              rowNumber: z.number().int().min(1),
              name: z.string().trim().min(1).max(200),
              // Ticket WK1a: das Bundesland kommt je Zeile aus der Datei.
              bundesland: bundeslandSchema,
              district: z.string().trim().min(1).max(200),
              postalCode: z.string().trim().regex(/^\d{5}$/u).nullable().default(null),
              population: z.number().int().min(0).max(20_000_000).nullable().default(null),
              officeLabel: z.string().trim().min(1).max(120),
              office: z.enum(MAYOR_OFFICES as unknown as [string, ...string[]]),
              firstName: z.string().trim().max(120).nullable().default(null),
              lastName: z.string().trim().max(120).nullable().default(null),
              termStartMonth: z.number().int().min(1).max(12),
              termStartYear: z.number().int().min(1900).max(2100),
              termStartDay: z.number().int().min(1).max(31).nullable().default(null),
              termYears: z.number().int().min(1).max(12).nullable().default(null),
            }),
          )
          .min(1)
          .max(MUNICIPALITY_MAX_ROWS),
        skipped: skippedSchema,
        duplicates: skippedSchema,
      })
      .parse(input),
  )

  .handler(async ({ context, data }): Promise<ImportReport> => {
    const { importMunicipalitiesOnServer } = await import("@/lib/wahlkalender.server");
    return importMunicipalitiesOnServer(context.supabase, context.userId, data as never);
  });

/* ====================================================== Ticket WK2 */

const runsAgainSchema = z.enum(RUNS_AGAIN_VALUES as unknown as [string, ...string[]]);

/** Gemeinsamer Schreibweg für die Gemeindeliste und die Kachel im Prüfeingang. */
const municipalityValuesSchema = z.object({
  firstName: z.string().trim().max(120).nullable().optional(),
  lastName: z.string().trim().max(120).nullable().optional(),
  postalCode: z.string().trim().regex(/^\d{5}$/u).nullable().optional(),
  population: z.number().int().min(0).max(20_000_000).nullable().optional(),
  officeLabel: z.string().trim().min(1).max(120).optional(),
  office: z.enum(MAYOR_OFFICES as unknown as [string, ...string[]]).optional(),
  termStartYear: z.number().int().min(1900).max(2100).optional(),
  termStartMonth: z.number().int().min(1).max(12).optional(),
  termStartDay: z.number().int().min(1).max(31).nullable().optional(),
  termYears: z.number().int().min(1).max(12).nullable().optional(),
  confirmedYear: z.number().int().min(1900).max(2100).nullable().optional(),
  confirmedMonth: z.number().int().min(1).max(12).nullable().optional(),
  confirmedDay: z.number().int().min(1).max(31).nullable().optional(),
  runsAgain: runsAgainSchema.optional(),
});

export const saveMunicipality = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    municipalityValuesSchema.extend({ id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ context, data }) => {
    const { saveMunicipalityOnServer } = await import("@/lib/wahlkalender.server");
    return saveMunicipalityOnServer(context.supabase, context.userId, data as never);
  });

export const listMunicipalityReviews = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { listReviewsOnServer } = await import("@/lib/wahlkalender.server");
    return listReviewsOnServer(context.supabase, context.userId);
  });

export const completeMunicipalityReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        municipalityId: z.string().uuid(),
        values: municipalityValuesSchema.optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { completeReviewOnServer } = await import("@/lib/wahlkalender.server");
    return completeReviewOnServer(context.supabase, context.userId, data as never);
  });

export const acceptMunicipalityReviewProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        municipalityId: z.string().uuid(),
        reviewId: z.string().uuid(),
        entryIndex: z.number().int().min(0).max(20),
        field: z.enum(PROPOSAL_FIELDS as unknown as [string, ...string[]]),
        proposed: z.string().trim().min(1).max(300),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { acceptReviewProposalOnServer } = await import("@/lib/wahlkalender.server");
    return acceptReviewProposalOnServer(context.supabase, context.userId, data as never);
  });

export const dismissMunicipalityReviewProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        municipalityId: z.string().uuid(),
        reviewId: z.string().uuid(),
        entryIndex: z.number().int().min(0).max(20),
        field: z.enum(PROPOSAL_FIELDS as unknown as [string, ...string[]]),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { dismissReviewProposalOnServer } = await import("@/lib/wahlkalender.server");
    return dismissReviewProposalOnServer(context.supabase, context.userId, data as never);
  });

/** Das X der Prüfkarte: verwirft alle offenen Fälle der Karte dauerhaft. */
export const dismissMunicipalityReviewTile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        municipalityId: z.string().uuid(),
        reviewIds: z.array(z.string().uuid()).min(1).max(200),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { dismissReviewTileOnServer } = await import("@/lib/wahlkalender.server");
    return dismissReviewTileOnServer(context.supabase, context.userId, data);
  });

export const listMunicipalityDistricts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ bundesland: bundeslandSchema.nullable().default(null) }).parse(input ?? {}),
  )
  .handler(async ({ context, data }): Promise<string[]> => {
    const { listDistrictsOnServer } = await import("@/lib/wahlkalender.server");
    return listDistrictsOnServer(context.supabase, context.userId, data as never);
  });

/* ====================================================== Ticket WK8 */

/**
 * Ticket WK8: Der Rechnungs-Lauf wird von Hand angestoßen. Er hat weiterhin
 * keinen Zeitplan.
 */
export const runMunicipalityReviewTriggers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { runReviewTriggersForStaff } = await import("@/lib/wahlkalender.server");
    return runReviewTriggersForStaff(context.supabase, context.userId);
  });

/**
 * Ticket WK8: Der Presse-Lauf läuft wöchentlich und lässt sich zusätzlich von
 * Hand anstoßen – derselbe fortsetzbare Lauf, kein zweiter Weg.
 */
export const runMunicipalityPressScan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { runPressScanForStaff } = await import("@/lib/wahlkalender-presse.server");
    return runPressScanForStaff(context.supabase, context.userId);
  });

